/**
 * EL-195 — o balão do "i" de ajuda dos campos do simulador (lote de 09/10/2026, item 1).
 *
 * POR QUE ISTO EXISTE
 *   Pedido: "Eliminar todas as tooltips que estão sem conteúdo dentro." O que o usuário via:
 *   "Clico no 'i' e nada abre". A dica morava no `title` nativo do `<span class="info">`, que
 *   só aparece parando o mouse ~1 s em cima — no clique, no toque e no teclado, nada. O "i"
 *   virou `<button>` (`fldRotulo`, no simulador) e o texto foi para `data-dica`, sem `title`:
 *   duas dicas para o mesmo "i" (a nativa e o balão) seria ruído.
 *
 * COMO SE COMPORTA
 *   · abre no clique, no toque e em Enter/Espaço (os três chegam como `click`);
 *   · com mouse, abre também ao parar em cima (atraso curto) e fecha ao sair — a não ser que
 *     a pessoa tenha clicado: aí fica até ela fechar (`acaoDoClique`);
 *   · um balão por vez; fecha ao clicar fora, Esc, rolar, mudar o tamanho da janela, tirar o
 *     foco do "i" ou trocar de tela (`desligarBalaoInfo`, chamado por `montagem.desmontar`).
 *
 * CICLO DE VIDA (a regra 7 da casa: tudo que se pendura, se tira)
 *   Os ouvintes são DELEGADOS no documento e NOMEADOS — o navegador só deduplica função
 *   nomeada, e a montagem do simulador roda a cada troca de cenário. `ligarBalaoInfo` chama
 *   `desligarBalaoInfo` antes de começar, e o nó do balão (no `<body>`, nunca no contêiner:
 *   o `backdrop-filter` da barra superior reancora qualquer descendente `fixed`) sai junto.
 *
 * E O NOME CORTADO: em notebook (1280–1536 px) o nome do campo passa a ganhar "…" no lugar de
 * empurrar o "i" para fora; parar o mouse no nome mostra o nome inteiro (`title` SÓ quando o
 * nome está cortado — medido, e remedido ao mudar o tamanho da janela e ao passar o mouse).
 */
import { h } from '../dom';
import { acaoDoClique, posicionarBalao, textoDaDica } from '../balao-posicao';
import type { ModoBalao } from '../balao-posicao';

const ID_BALAO = 'balao-info';
const ID_TEXTO = 'balao-info-texto';
const SELETOR_I = 'button.info';
/** Parar o mouse no "i": curto o bastante para parecer imediato, longo para não piscar ao passar. */
const ATRASO_MOUSE_MS = 250;
/** Sair do "i" rumo ao balão atravessa um vão de 8 px — sem tolerância, o balão fugiria do mouse. */
const TOLERANCIA_SAIDA_MS = 140;
/**
 * Clicar num "i" meio escondido faz o navegador rolá-lo para dentro da vista ao dar o foco — e
 * essa rolagem chega DEPOIS do clique. Sem a carência, o balão abria e fechava no mesmo gesto.
 */
const CARENCIA_ROLAGEM_MS = 200;

interface Aberto {
  i: HTMLButtonElement;
  modo: ModoBalao;
  desde: number;
}

let aberto: Aberto | null = null;
let timerAbrir: number | undefined;
let timerFechar: number | undefined;
let pedidoMedida = 0;

/* ------------------------------------------------------------------ o nó */

function balaoExistente(): HTMLElement | null {
  return document.getElementById(ID_BALAO);
}

/** Cria o balão na primeira abertura (não na montagem): quem nunca clica num "i" não ganha nó. */
function obterBalao(): HTMLElement {
  const ja = balaoExistente();
  if (ja !== null) return ja;
  const novo = h(
    'div',
    { id: ID_BALAO, class: 'balao-info', role: 'tooltip', 'data-el': 'EL-195', hidden: true },
    /* A etiqueta do "Mostrar IDs" vem pronta, como na marcação da v67: o balão nasce depois
       da passada do `marcarIds`, e ninguém a poria. */
    h('span', { class: 'elb', 'data-el': 'EL-195' }, 'EL-195'),
    h('span', { id: ID_TEXTO, class: 'balao-txt' }),
  );
  document.body.appendChild(novo);
  return novo;
}

const dentro = (no: EventTarget | null, el: Element | null): boolean =>
  el !== null && no instanceof Node && el.contains(no);

function limparTimers(): void {
  window.clearTimeout(timerAbrir);
  window.clearTimeout(timerFechar);
  timerAbrir = undefined;
  timerFechar = undefined;
}

/* ------------------------------------------------------------------ abrir / fechar */

function abrir(i: HTMLButtonElement, modo: ModoBalao): void {
  const texto = textoDaDica(i.dataset.dica);
  limparTimers();
  if (texto === null || !i.isConnected) {
    fecharBalaoInfo();
    return;
  }
  if (aberto !== null && aberto.i !== i) marcarFechado(aberto.i);

  const balao = obterBalao();
  const alvo = balao.querySelector<HTMLElement>('.balao-txt');
  if (alvo !== null) alvo.textContent = texto;
  /* Mede ainda transparente (sem `.show`, opacidade 0) e só então mostra: o tamanho depende
     do texto, e a posição do tamanho. */
  balao.hidden = false;
  balao.classList.remove('show');
  aberto = { i, modo, desde: performance.now() };
  i.setAttribute('aria-expanded', 'true');
  i.setAttribute('aria-describedby', ID_TEXTO);
  if (!posicionar()) return;
  /* Um quadro depois, para a transição de entrada acontecer a partir do estado escondido. */
  requestAnimationFrame(() => {
    if (aberto?.i === i) balao.classList.add('show');
  });
}

function marcarFechado(i: HTMLButtonElement): void {
  i.setAttribute('aria-expanded', 'false');
  i.removeAttribute('aria-describedby');
}

/** Fecha o balão aberto, se houver. Idempotente. */
export function fecharBalaoInfo(): void {
  limparTimers();
  esconder();
}

function esconder(): void {
  if (aberto !== null) marcarFechado(aberto.i);
  aberto = null;
  const balao = balaoExistente();
  if (balao !== null) {
    balao.classList.remove('show');
    balao.hidden = true;
  }
}

/**
 * Põe o balão junto do "i". Devolve `false` (e fecha) quando o "i" sumiu — a tela foi
 * redesenhada por baixo ou a aba dele foi escondida: um balão apontando para o nada é pior
 * que nenhum.
 */
function posicionar(): boolean {
  const balao = balaoExistente();
  if (aberto === null || balao === null) return false;
  const r = aberto.i.getBoundingClientRect();
  if (!aberto.i.isConnected || (r.width === 0 && r.height === 0)) {
    fecharBalaoInfo();
    return false;
  }
  const p = posicionarBalao(
    { esquerda: r.left, topo: r.top, largura: r.width, altura: r.height },
    { largura: balao.offsetWidth, altura: balao.offsetHeight },
    limites(),
  );
  balao.style.left = `${Math.round(p.esquerda)}px`;
  balao.style.top = `${Math.round(p.topo)}px`;
  balao.style.setProperty('--seta-x', `${Math.round(p.setaX)}px`);
  balao.classList.toggle('em-cima', p.lado === 'cima');
  balao.classList.toggle('em-baixo', p.lado === 'baixo');
  return true;
}

/**
 * O espaço onde o balão pode ficar: abaixo da barra superior fixa e acima da faixa de
 * ambiente do rodapé. Com uma tabela em tela cheia, a sobreposição cobre a barra superior —
 * aí a janela inteira vale.
 */
function limites() {
  const largura = document.documentElement.clientWidth || window.innerWidth;
  let topoLivre = 0;
  if (!document.body.classList.contains('tem-tela-cheia')) {
    const barra = document.querySelector('header.top');
    if (barra !== null) topoLivre = Math.max(0, barra.getBoundingClientRect().bottom);
  }
  let baseLivre = window.innerHeight;
  const faixa = document.querySelector('.faixa-ambiente');
  if (faixa !== null) {
    const rf = faixa.getBoundingClientRect();
    if (rf.height > 0) baseLivre = Math.min(baseLivre, rf.top);
  }
  return { largura, topoLivre, baseLivre };
}

/* ------------------------------------------------------------------ o nome cortado */

/** `title` com o nome inteiro SÓ quando o nome está cortado. Repetir um nome visível é ruído. */
function medirNome(el: HTMLElement): void {
  if (el.getClientRects().length === 0) return;   /* aba escondida: mede quando aparecer */
  const nome = (el.textContent ?? '').trim();
  if (nome !== '' && el.scrollWidth > el.clientWidth) {
    if (el.getAttribute('title') !== nome) el.setAttribute('title', nome);
  } else {
    el.removeAttribute('title');
  }
}

/** Mede todos os nomes de campo visíveis em `raiz`. Chamada na montagem e ao mudar a janela. */
export function marcarNomesCortados(raiz: ParentNode = document): void {
  raiz.querySelectorAll<HTMLElement>('.lbl-nome').forEach(medirNome);
}

/* ------------------------------------------------------------------ os ouvintes (NOMEADOS) */

function aoClicar(e: MouseEvent): void {
  const i = (e.target as Element | null)?.closest?.(SELETOR_I);
  if (!(i instanceof HTMLButtonElement)) return;
  const acao = acaoDoClique(aberto === null ? null : { mesmoI: aberto.i === i, modo: aberto.modo });
  if (acao === 'fechar') fecharBalaoInfo();
  else if (acao === 'fixar' && aberto !== null) {
    limparTimers();
    aberto.modo = 'clique';
  } else abrir(i, 'clique');
}

function aoApertar(e: PointerEvent): void {
  if (aberto === null) return;
  if (dentro(e.target, aberto.i) || dentro(e.target, balaoExistente())) return;
  fecharBalaoInfo();
}

function aoPassar(e: PointerEvent): void {
  const alvo = e.target as Element | null;
  if (alvo === null || typeof alvo.closest !== 'function') return;
  const nome = alvo.closest<HTMLElement>('.lbl-nome');
  if (nome !== null) medirNome(nome);
  if (e.pointerType !== 'mouse') return;   /* toque e caneta abrem pelo clique */

  if (aberto !== null && (dentro(alvo, aberto.i) || dentro(alvo, balaoExistente()))) {
    window.clearTimeout(timerFechar);
    timerFechar = undefined;
    return;
  }
  const i = alvo.closest(SELETOR_I);
  if (!(i instanceof HTMLButtonElement)) return;
  /* O fixado pelo clique manda: passar por outro "i" não o troca. */
  if (aberto !== null && aberto.modo === 'clique') return;
  window.clearTimeout(timerAbrir);
  timerAbrir = window.setTimeout(() => abrir(i, 'mouse'), ATRASO_MOUSE_MS);
}

function aoSair(e: PointerEvent): void {
  if (e.pointerType !== 'mouse') return;
  const para = e.relatedTarget;
  const i = (e.target as Element | null)?.closest?.(SELETOR_I) ?? null;
  if (i !== null && !dentro(para, i)) {
    window.clearTimeout(timerAbrir);   /* passou reto: não abre */
    timerAbrir = undefined;
  }
  if (aberto === null || aberto.modo !== 'mouse') return;
  const balao = balaoExistente();
  const saiuDoPar = (dentro(e.target, aberto.i) || dentro(e.target, balao))
    && !dentro(para, aberto.i) && !dentro(para, balao);
  if (!saiuDoPar) return;
  window.clearTimeout(timerFechar);
  timerFechar = window.setTimeout(fecharDoMouse, TOLERANCIA_SAIDA_MS);
}

/**
 * O fechamento por SAIR com o mouse. Não é o `fecharBalaoInfo`: aquele zera também o atraso de
 * abrir — e quem passa de um "i" direto para o vizinho da mesma linha agendou a abertura do
 * vizinho ANTES deste fechamento chegar. Zerar ali era o vizinho nunca abrir.
 */
function fecharDoMouse(): void {
  timerFechar = undefined;
  if (aberto !== null && aberto.modo === 'mouse') esconder();
}

/** Esc fecha SÓ o balão: na fase de captura da janela, antes da tela cheia e dos modais. */
function aoTeclar(e: KeyboardEvent): void {
  if (e.key !== 'Escape' || aberto === null) return;
  e.stopPropagation();
  e.preventDefault();
  fecharBalaoInfo();
}

/**
 * Tab para fora do "i" fecha o balão — sem isto ele ficaria sobre o campo que recebeu o foco.
 * Só quando o foco FOI para outro elemento: clicar no texto do balão (para copiar) ou trocar
 * de janela também tira o foco do "i", mas para lugar nenhum — e aí fechar seria fugir da mão.
 * O clique fora de verdade já é do `aoApertar`.
 */
function aoPerderFoco(e: FocusEvent): void {
  if (aberto === null || e.target !== aberto.i || e.relatedTarget === null) return;
  if (dentro(e.relatedTarget, balaoExistente())) return;
  fecharBalaoInfo();
}

function aoRolar(): void {
  if (aberto === null) return;
  if (performance.now() - aberto.desde < CARENCIA_ROLAGEM_MS) {
    posicionar();
    return;
  }
  fecharBalaoInfo();
}

function aoRedimensionar(): void {
  fecharBalaoInfo();
  cancelAnimationFrame(pedidoMedida);
  pedidoMedida = requestAnimationFrame(() => marcarNomesCortados());
}

/* ------------------------------------------------------------------ ciclo de vida */

/** Liga o balão. Chamada a cada montagem do simulador; começa desligando o que houver. */
export function ligarBalaoInfo(): void {
  desligarBalaoInfo();
  document.addEventListener('click', aoClicar, true);
  document.addEventListener('pointerdown', aoApertar, true);
  document.addEventListener('pointerover', aoPassar);
  document.addEventListener('pointerout', aoSair);
  document.addEventListener('focusout', aoPerderFoco);
  document.addEventListener('scroll', aoRolar, true);
  window.addEventListener('keydown', aoTeclar, true);
  window.addEventListener('resize', aoRedimensionar);
}

/** Solta TUDO que `ligarBalaoInfo` pendurou. Idempotente. */
export function desligarBalaoInfo(): void {
  fecharBalaoInfo();
  cancelAnimationFrame(pedidoMedida);
  document.removeEventListener('click', aoClicar, true);
  document.removeEventListener('pointerdown', aoApertar, true);
  document.removeEventListener('pointerover', aoPassar);
  document.removeEventListener('pointerout', aoSair);
  document.removeEventListener('focusout', aoPerderFoco);
  document.removeEventListener('scroll', aoRolar, true);
  window.removeEventListener('keydown', aoTeclar, true);
  window.removeEventListener('resize', aoRedimensionar);
  /* Pelo id, e todos: se uma montagem anterior deixou órfão, sai aqui. */
  document.querySelectorAll(`#${ID_BALAO}`).forEach((n) => n.remove());
}
